import { useState } from 'react';
import { AppBar, Toolbar, Typography, IconButton, Avatar, Box, Menu, MenuItem, Chip, useMediaQuery, useTheme, FormControl, Select } from '@mui/material';
import { Menu as MenuIcon, AccountCircle, Logout, Settings, OpenInNew, Close } from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useApp } from '../../context/AppContext';

const DRAWER_WIDTH = 260;

const Navbar = ({ onMobileMenuClick }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const { modo, cajaEstado, setCajaEstado } = useApp();
  const [anchorEl, setAnchorEl] = useState(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // Ocultar navbar en rutas de billing en desktop
  const isBillingRoute = location.pathname.startsWith('/billing');

  // Obtener módulos autorizados
  const getAuthorizedModules = () => {
    try {
      const stored = localStorage.getItem('nx2026_authorized_modules');
      return stored ? JSON.parse(stored) : [];
    } catch {
      return [];
    }
  };

  const authorizedModules = getAuthorizedModules();
  const hasPOS = authorizedModules.includes('POS');

  const handleMenu = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    handleClose();
    logout();
    navigate('/login');
  };

  const handleCajaChange = (event) => {
    setCajaEstado(event.target.value);
  };

  // Color del chip de caja
  const cajaColor = cajaEstado === 'abierta' ? 'success' : 'error';

  // Ocultar navbar en rutas de billing en desktop
  if (!isMobile && isBillingRoute) {
    return null;
  }

  return (
    <AppBar 
      position="fixed" 
      sx={{ 
        ml: isMobile ? 0 : `${DRAWER_WIDTH}px`, 
        width: isMobile ? '100%' : `calc(100% - ${DRAWER_WIDTH}px)`,
        backgroundColor: 'primary.main',
      }}
    >
      <Toolbar>
        {/* Botón de menú móvil */}
        {isMobile && (
          <IconButton
            color="inherit"
            edge="start"
            onClick={onMobileMenuClick}
            sx={{ mr: 2 }}
          >
            <MenuIcon />
          </IconButton>
        )}
        
        {/* Selector de estado de caja (solo para usuarios con acceso a POS) */}
        {hasPOS && modo === 'POS' && !isMobile && (
          <Box sx={{ display: 'flex', alignItems: 'center', mr: 2, ml: isMobile ? 0 : 'auto' }}>
            <Typography variant="body2" sx={{ mr: 1, display: { xs: 'none', sm: 'block' } }}>
              Caja:
            </Typography>
            <FormControl size="small" sx={{ minWidth: 100 }}>
              <Select
                value={cajaEstado}
                onChange={handleCajaChange}
                sx={{ 
                  color: 'white',
                  '.MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.3)' },
                  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.5)' },
                  '.MuiSvgIcon-root': { color: 'white' }
                }}
              >
                <MenuItem value="abierta">
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <OpenInNew fontSize="small" color="success" />
                    Abierta
                  </Box>
                </MenuItem>
                <MenuItem value="cerrada">
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Close fontSize="small" color="error" />
                    Cerrada
                  </Box>
                </MenuItem>
              </Select>
            </FormControl>
          </Box>
        )}

        {/* Chip de estado de caja en móvil */}
        {hasPOS && modo === 'POS' && isMobile && (
          <Chip 
            size="small"
            icon={cajaEstado === 'abierta' ? <OpenInNew /> : <Close />}
            label={cajaEstado === 'abierta' ? 'Abierta' : 'Cerrada'}
            color={cajaColor}
            sx={{ mr: 1 }}
          />
        )}
        
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 'auto' }}>
          <Typography variant="body2" sx={{ display: { xs: 'none', sm: 'block' } }}>
            {user?.name || user?.email || 'Usuario'}
          </Typography>
          <IconButton
            size="large"
            aria-label="account of current user"
            aria-controls="menu-appbar"
            aria-haspopup="true"
            onClick={handleMenu}
            color="inherit"
          >
            <Avatar sx={{ width: 32, height: 32, bgcolor: 'secondary.main' }}>
              {(user?.name || user?.email || 'U').charAt(0).toUpperCase()}
            </Avatar>
          </IconButton>
          <Menu
            id="menu-appbar"
            anchorEl={anchorEl}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'right',
            }}
            keepMounted
            transformOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
            open={Boolean(anchorEl)}
            onClose={handleClose}
          >
            <MenuItem onClick={() => { handleClose(); navigate('/settings'); }}>
              <Settings sx={{ mr: 1 }} /> Configuración
            </MenuItem>
            <MenuItem onClick={handleLogout}>
              <Logout sx={{ mr: 1 }} /> Cerrar Sesión
            </MenuItem>
          </Menu>
        </Box>
      </Toolbar>
    </AppBar>
  );
};

export default Navbar;
